import api from "./api.service";
import { tokenService } from "./token.service";

export interface OrderDetails {
  firstName: string;
  lastName: string;
  email: string;
  PhoneNumber: string;
  KindOfDNI: string;
  DNI: string;
  AddressLine1: string;
  AddressLine2: string;
  City: string;
  State: string;
  Municipality: string;
  ZipCode: string;
  iso2Country: string;
  isThirdPerson: boolean;
}

export interface ParcelData {
  serviceType: string;
  height: number;
  width: number;
  length: number;
  weight: number;
  parcelOrdDoc: string;
  currency: string;
  unitSystem: string;
  fob: number;
  content: string;
  isFragile?: boolean;
}

export interface ExternalServiceSelection {
  serviceName: string;
  use: boolean;
}

export interface CreateOrderRequest {
  idCarrier: number;
  idCarrierService?: number | null;
  senderDetails: OrderDetails;
  receiverDetails: OrderDetails;
  parcelData: ParcelData;
  externalServices?: {
    serviceName: string;
    use: boolean;
  }[];
  idExternalCourier?: number | null;
  idExternalCourierService?: number | null;
  idExternalAdditionalService?: number | null;
  paymentMethodId?: string;
  pickupDate?: string;
  hasBattery: boolean;
  fragile?: boolean;
  liquid?: boolean;
  DocumentoBase64?: string;
  DocumentoExt?: string;
  packages?: Array<{
    height: number;
    width: number;
    length: number;
    weight: number;
    fob?: number;
    content?: string;
    isFragile?: boolean;
  }>;
}

export interface CreateOrderResponse {
  statusCode: number;
  message: string;
  clientSecret?: string;
  paymentIntentId?: string;
  orderId?: number;
  data: {
    id?: number;
    idOrder?: number;
    trackingNumber: string;
    totalCost: number;
    iso3currency: string;
    orderNumber: string;
    carrier: {
      carrierName: string;
      carrierTrackingNumber: string;
      carrierBase64: string;
      carrierCost: number;
      iso3currency: string;
    };
    externalService: {
      serviceName: string;
      serviceTrackingNumber: string;
      serviceBase64: string;
      serviceCost: number;
      iso3currency: string;
    }[];
  };
}

export interface ParcelService {
  id: number;
  serviceDetail: string;
  serviceCost: number;
  isO3Currency: string;
}

export interface ParcelDataDetail {
  id: number;
  tracking?: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  content: string;
  weightUnit: string;
  sizeUnit: string;
  serviceCost: number;
  fob: number;
  isO3Currency: string;
  isFragile?: boolean;
  parcelServices: ParcelService[];
}

export interface OrderExternalService {
  id: number;
  serviceName: string;
  externalReference: string;
  base64ExternalService: string;
  codeRef?: string;
}

export interface CustomerData {
  country: string;
  firstName: string;
  lastName: string;
  kindOfDNI: string;
  dni: string;
  phoneNumber: string;
}

export interface OrderData {
  id: number;
  tracking: string;
  serviceCostTotal: number;
  isO3Currency: string;
  idCustomer: number;
  idCarrier: number;
  carrierName: string;
  lastEventStatus: string | null;
  sender: OrderAddressData;
  receiver: OrderAddressData;
  parcelData: ParcelDataDetail[];
  orderExternalService: OrderExternalService[];
  base64Carrier: string;
  customer?: CustomerData;
  base64Pdf?: string | null;
  hasBattery?: boolean;
}

export interface OrderAddressData {
  addressLine: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  firstName?: string;
  lastName?: string;
  kindOfDNI?: string;
  dni?: string;
  phoneNumber?: string;
  isThirdPerson?: boolean;
}

export interface CustomerOrder {
  id: number;
  trackingNumber: string;
  orderNumber: string;
  orderStatus: string | null;
  creationDate: string;
  carrierName: string;
  carrierId: number;
  serviceName?: string;
  reciberName: string;
  reciberDestiny: string;
  isoDestiny: string;
  weight: number;
  weightUnit: string;
  parcelCount: number;
  isPaid: boolean;
  isVoided?: boolean;
  isRefunded?: boolean;
}

export interface GetCustomerOrdersResponse {
  statusCode: number;
  message: string;
  data: CustomerOrder[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export interface GetCustomerOrdersParams {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  idCarrier?: number;
  national?: boolean;
  SearchString?: string;
}

export interface OrderResumeResponse {
  statusCode: number;
  message: string;
  data: {
    id?: number;
    trackingNumber: string;
    carrierName: string;
    carrierId: number;
    reciberName: string;
    reciberDestiny: string;
    weight: number;
    weightUnit: string;
    content: string;
    hasAdditionalService: boolean;
    externalService?: any[];
  };
  base64: string;
  base64externalReference: string | null;
  base64Pdf?: string;
}

export interface GetOrderResponse {
  statusCode: number;
  message: string;
  data: OrderData;
}

export interface OrderHistoryEvent {
  eventStatus: string;
  date: string;
  source?: string;
}

export interface ExternalReferenceEvent {
  event: string;
  location: string | null;
  date: string;
}

export interface ExternalReferenceTrack {
  externalReference: string;
  serviceName: string;
  events: ExternalReferenceEvent[];
}

export interface GetOrderHistoryResponse {
  statusCode: number;
  message: string;
  data: {
    id: number;
    lastEventStatus: string | null;
    lastEventDate: string | null;
    tracking: string;
    events: OrderHistoryEvent[];
    carrierName?: string;
    carrierTracking?: string;
    externalReference: {
      serviceName: string;
      externalReference: string;
    } | null;
  };
  externalReferenceTrack: ExternalReferenceTrack | null;
}

export interface GetOrderSummaryPdfResponse {
  statusCode: number;
  message: string;
  base64Pdf: string;
}

class OrderService {
  async createOrder(payload: CreateOrderRequest): Promise<CreateOrderResponse> {
    const token = tokenService.getToken();
    try {
      const response = await api.post<CreateOrderResponse>(
        "/create-order",
        payload,
        {
          headers: {
            Authorization: `${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    }
  }

  async getOrder(idOrder: number | string): Promise<GetOrderResponse> {
    const token = tokenService.getToken();
    try {
      const response = await api.get<GetOrderResponse>(`/get-order`, {
        params: { idOrder },
        headers: {
          Authorization: `${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching order:", error);
      throw error;
    }
  }

  async getOrderResume(orderNumber: string): Promise<OrderResumeResponse> {
    const token = tokenService.getToken();
    try {
      const response = await api.get<OrderResumeResponse>("/get-order-resume", {
        params: { orderNumber },
        headers: {
          Authorization: `${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching order resume:", error);
      throw error;
    }
  }

  async getCustomerOrders(
    params?: GetCustomerOrdersParams,
  ): Promise<GetCustomerOrdersResponse> {
    const token = tokenService.getToken();
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        if (params.page !== undefined)
          queryParams.append("page", params.page.toString());
        if (params.pageSize !== undefined)
          queryParams.append("pageSize", params.pageSize.toString());
        if (params.startDate) queryParams.append("startDate", params.startDate);
        if (params.endDate) queryParams.append("endDate", params.endDate);
        if (params.idCarrier !== undefined)
          queryParams.append("idCarrier", params.idCarrier.toString());
        if (params.national !== undefined)
          queryParams.append("national", params.national.toString());
        if (params.SearchString)
          queryParams.append("SearchString", params.SearchString);
      }

      const response = await api.get<GetCustomerOrdersResponse>(
        `/get-customer-order?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching customer orders:", error);
      throw error;
    }
  }

  async getOrderHistory(
    trackingNumber: string,
  ): Promise<GetOrderHistoryResponse> {
    try {
      const response = await api.get<GetOrderHistoryResponse>(
        "/get-order-history",
        {
          params: { trackingNumber },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching order history:", error);
      throw error;
    }
  }

  async getOrderHistoryById(
    idOrder: number | string,
  ): Promise<GetOrderHistoryResponse> {
    try {
      const response = await api.get<GetOrderHistoryResponse>(
        "/get-order-history-by-order-id",
        {
          params: { idOrder },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching order history by ID:", error);
      throw error;
    }
  }

  async getOrderSummaryPdf(idOrder: number | string): Promise<GetOrderSummaryPdfResponse> {
    const token = tokenService.getToken();
    try {
      const response = await api.get<GetOrderSummaryPdfResponse>(
        "/get-order-summary-pdf",
        {
          params: { idOrder },
          headers: {
            Authorization: `${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching order summary PDF:", error);
      throw error;
    }
  }
}

export const orderService = new OrderService();
